import { useCallback, useContext, useEffect, useRef } from 'react'
import { MountContext } from '../components/Mount'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import scrollTo from './scrollTo'
import get from 'lodash/get'
import { GET_PROMOCODE_REJECT, POST_SUBSCRIPTIONS_PROMOCODE_REJECT } from '../constants/Api'
import Alert from '../components/Alert'
import dayjs from 'dayjs'
import scrollToElement from './scrollToElement'

const usePromocodeLogic = ({
  promocode,
  setPromocode,
  setErrors,
  setLoading,
  getPromocode,
  activeSubscriptionTypes,
  subscriptions,
  setDiscounts,
  newSubscriptionEl,
  postSubscriptionsPromocode,
  getAccount,
  setSuccess
}) => {
  const viewEl = useRef(null)
  const mount = useContext(MountContext)
  const location = useLocation()
  const history = useHistory()
  const paymentEvent = new CustomEvent('payment')

  const applyPromocodeWithCallback = useCallback(applyPromocode, [
    activeSubscriptionTypes,
    getPromocode,
    mount,
    newSubscriptionEl,
    postPromocode,
    setDiscounts,
    setErrors,
    setLoading,
    setPromocode,
    subscriptions.length,
    viewEl
  ])

  useEffect(() => {
    const query = queryString.parse(location.search)
    if (query.scroll_to_promocode && viewEl.current) {
      history.replace({
        search: ''
      })
      if (query.scroll_to_promocode === '1') {
        setTimeout(() => {
          scrollTo(viewEl.current.getBoundingClientRect().top + window.pageYOffset - 20, 500)
        }, 300)
      } else {
        setPromocode(query.scroll_to_promocode)
        applyPromocodeWithCallback(query.scroll_to_promocode, true)
      }
    }
  }, [setPromocode, applyPromocodeWithCallback, history, location.search, viewEl])

  async function applyPromocode(promocodeValue, scrollToPromocodeFieldOnError = false) {
    setErrors([])

    if (promocodeValue && promocodeValue.length > 0) {
      setLoading(true)
      const getResponse = await getPromocode(promocodeValue)
      setLoading(false)

      const kind = get(getResponse, 'payload.data.attributes.kind')
      const subscriptionDurationInDays = get(getResponse, 'payload.data.attributes.subscription_duration_in_days')
      const kidsType = get(getResponse, 'payload.data.attributes.kids_app') ? 'kids' : null
      const radioType = get(getResponse, 'payload.data.attributes.radio_app') ? 'radio' : null
      const notAllowedError = get(getResponse, 'payload.data.attributes.not_allowed_error')
      const types = [kidsType, radioType]
      const elementId = kidsType && radioType ? 'pack' : kidsType ? 'kids' : 'radio'
      const isSubscriptionsOfPromocodeType = activeSubscriptionTypes.filter(subscription =>
        types.includes(subscription)
      )
      const subscriptionExpiresAt = get(getResponse, 'payload.data.attributes.subscription_expires_at')

      if (getResponse.type === GET_PROMOCODE_REJECT || notAllowedError) {
        setErrors([
          {
            title:
              notAllowedError ||
              'К сожалению, введенный промокод не существует или больше не действителен. Если произошла какая-то ошибка, напишите нам, пожалуйста, на почту radio@arzamas.academy'
          }
        ])
        if (window.SubscriptionAnalytics) {
          window.SubscriptionAnalytics.trackPromoCodeApplicationFailed(
            promocodeValue,
            notAllowedError ? 'NOT_ALLOWED_CODE' : 'INVALID_CODE'
          )
        }
        if (scrollToPromocodeFieldOnError) {
          setTimeout(() => {
            const getTo = () => viewEl.current.getBoundingClientRect().top + window.pageYOffset - 20
            scrollTo(getTo(), 500).then(() => {
              const to = getTo()
              if (Math.trunc(to) !== Math.trunc(window.pageYOffset)) {
                scrollTo(to, 500)
              }
            })
          }, 100)
        }
      } else if (
        isSubscriptionsOfPromocodeType.length &&
        elementId !== 'pack' &&
        (kind === 'promo' || kind === 'gift_certificate') &&
        Boolean(subscriptionDurationInDays)
      ) {
        console.log('Промокод на период', subscriptionDurationInDays)
        mount.push(Alert, {
          props: {
            title: 'Внимание!',
            message: 'Промокод заработает после окончания действующей подписки.',
            buttons: [
              {
                text: 'ОК',
                onClick: () => {
                  mount.pop()
                  postPromocode(elementId)
                }
              },
              { type: 'cancel', text: 'Отмена', onClick: () => mount.pop() }
            ]
          }
        })
      } else if (subscriptions.length === 0 && (kind === 'promo' || kind === 'gift_certificate')) {
        console.log('Промокод до даты', subscriptionExpiresAt)
        const postResponse = await postPromocode()

        const expiresAt = get(postResponse, 'payload.data.attributes.expires_at')
        const promocodeType = get(postResponse, 'payload.data.attributes.product')

        if (typeof expiresAt === 'number') {
          const formattedExpiresAt = dayjs.unix(expiresAt).format('DD.MM.YYYY')
          const isYear3000Plus = dayjs.unix(expiresAt).year() >= 3000
          let popupText = ''

          switch (promocodeType) {
            case 'kids_and_radio_promo':
              popupText = `Зайдите под своей учетной записью, <strong>авторизовавшись тем же способом, что и на сайте</strong>, в приложения «<a class="account-alert__link" href="https://deed3.app.goo.gl/?link=https%3A%2F%2Farzamas.academy%2Fradio%3Faz_action%3Dnavigate%26az_nav_method%3Dreset%26az_screen%3DMain&apn=com.azradio&isi=1261860228&ibi=com.arzamas.radio&ius=arzamasradio&ofl=https%3A%2F%2Farzamas.academy%2Fradio" target="_blank">Радио Arzamas</a>» и «<a class="account-alert__link" href="https://goosegoosegg.page.link/?link=https%3A%2F%2Farzamas.academy%2Fradio%3Faz_action%3Dnavigate%26az_nav_method%3Dreset%26az_screen%3DMain&apn=gg.goosegoose&isi=1534779649&ibi=gg.goosegoose&ofl=https%3A%2F%2Farzamas.academy%2Fgoosegoose" target="_blank">Гусьгусь</a>» на мобильном телефоне, чтобы получить полный доступ ко всем материалам.`
              break
            case 'radio_promo':
              popupText = `Зайдите под своей учетной записью, <strong>авторизовавшись тем же способом, что и на сайте</strong>, в приложение «<a class="account-alert__link" href="https://deed3.app.goo.gl/?link=https%3A%2F%2Farzamas.academy%2Fradio%3Faz_action%3Dnavigate%26az_nav_method%3Dreset%26az_screen%3DMain&apn=com.azradio&isi=1261860228&ibi=com.arzamas.radio&ius=arzamasradio&ofl=https%3A%2F%2Farzamas.academy%2Fradio" target="_blank">Радио Arzamas</a>» на мобильном телефоне, чтобы получить полный доступ ко всем материалам.`
              break
            case 'kids_promo':
              popupText = `Зайдите под своей учетной записью, <strong>авторизовавшись тем же способом, что и на сайте</strong>, в приложение «<a class="account-alert__link" href="https://goosegoosegg.page.link/?link=https%3A%2F%2Farzamas.academy%2Fradio%3Faz_action%3Dnavigate%26az_nav_method%3Dreset%26az_screen%3DMain&apn=gg.goosegoose&isi=1534779649&ibi=gg.goosegoose&ofl=https%3A%2F%2Farzamas.academy%2Fgoosegoose" target="_blank">Гусьгусь</a>» на мобильном телефоне, чтобы получить полный доступ ко всем материалам.`
              break
            default:
          }

          const popupTextWithBreak = popupText ? `<br />${popupText}` : ''

          mount.push(Alert, {
            props: {
              title: 'Ура, все получилось!',
              message: isYear3000Plus
                ? `Вы активировали бесконечную подписку.${popupTextWithBreak}`
                : `Ваша подписка действует до <strong>${formattedExpiresAt}</strong>${popupTextWithBreak}`,
              buttons: [
                {
                  text: 'ОК',
                  onClick: () => {
                    mount.pop()
                    scrollToElement(elementId)
                  }
                }
              ]
            }
          })
        }
        // проверяем наличие подписок для которых предназначен промокод
      } else if (isSubscriptionsOfPromocodeType.length && kind === 'discount') {
        console.log(
          'Промокод на скидку, есть конфликтующая подписка',
          get(getResponse, 'payload.data.attributes.discounts')
        )
        if (window.SubscriptionAnalytics) {
          window.SubscriptionAnalytics.trackPromoCodeApplicationFailed(
            promocodeValue,
            'ACCOUNT_HAS_CONFLICTING_SUBSCRIPTION'
          )
        }
        mount.push(Alert, {
          props: {
            title: 'Внимание!',
            message: 'Скидка действительна только для пользователей без действующей подписки.',
            buttons: [{ text: 'ОК', onClick: () => mount.pop() }]
          }
        })
      } else if (isSubscriptionsOfPromocodeType.length === 0 && kind === 'discount') {
        console.log(
          'Промокод на скидку, нет конфликтующих подписок',
          get(getResponse, 'payload.data.attributes.discounts')
        )
        setDiscounts({
          code: get(getResponse, 'payload.data.attributes.code'),
          ...get(getResponse, 'payload.data.attributes.discounts')
        })
        if (newSubscriptionEl.current) {
          scrollTo(newSubscriptionEl.current.offsetTop - 20, 500)
        }
        scrollToElement('prices')
        setPromocode('')
        if (window.SubscriptionAnalytics) {
          window.SubscriptionAnalytics.trackPromoCodeApplicationSucceeded(promocodeValue)
        }
        setSuccess(true)
      } else {
        console.log('Промокод до даты, есть другие подписки', subscriptionExpiresAt)
        // works if subscriptions.length > 0 && (kind === 'promo' || kind === 'gift_certificate') && Boolean(subscriptionExpiresAt)
        postPromocode(elementId)
      }
    }
  }

  async function postPromocode(id) {
    setLoading(true)
    const response = await postSubscriptionsPromocode(promocode)
    setLoading(false)

    const errorsArray = get(response, 'payload.response.errors')
    const expiresAt = get(response, 'payload.data.attributes.expires_at')
    const isReject =
      response?.error === true ||
      response?.type === POST_SUBSCRIPTIONS_PROMOCODE_REJECT ||
      (Array.isArray(errorsArray) && errorsArray.length > 0)
    const envelopeMissing = !isReject && typeof expiresAt !== 'number'

    if (isReject || envelopeMissing) {
      if (Array.isArray(errorsArray) && errorsArray.length > 0) {
        setErrors(errorsArray)
      } else {
        const p = response?.payload
        console.warn('[promocode-debug] postSubscriptions failed', {
          reason: isReject ? 'rejected' : 'envelope_missing',
          payloadName: p?.name,
          status: p?.status,
          statusText: p?.statusText,
          message: p?.message,
          hasData: Boolean(p?.data),
          hasResponseObject: typeof p?.response === 'object' && p?.response !== null,
          expiresAt
        })
        setErrors([
          {
            title:
              'Не удалось применить промокод. Проверьте соединение и попробуйте ещё раз или напишите нам на radio@arzamas.academy.'
          }
        ])
      }
      if (window.SubscriptionAnalytics) {
        const failureReason = Array.isArray(errorsArray) && errorsArray.length > 0
          ? 'FAILED_TO_CREATE_PROMO_SUBSCRIPTION'
          : isReject
            ? 'REQUEST_REJECTED'
            : 'UNEXPECTED_RESPONSE'
        window.SubscriptionAnalytics.trackPromoCodeApplicationFailed(promocode, failureReason)
      }
    } else {
      setLoading(true)
      if (window.SubscriptionAnalytics) {
        window.SubscriptionAnalytics.trackPromoCodeApplicationSucceeded(promocode)
      }
      await getAccount()
      setLoading(false)
      setPromocode('')
      // setSuccess(true)

      if (id) {
        scrollToElement(id)
      }

      // делаем pack активным после применения промокода
      document.dispatchEvent(paymentEvent)
    }
    return response
  }

  return { applyPromocode: applyPromocodeWithCallback, viewEl }
}

export default usePromocodeLogic
